import React, { cloneElement, isValidElement, useId } from "react";

export const cx = (...classes) => classes.filter(Boolean).join(" ");

const CONTROL_BASE =
  "w-full rounded-control border bg-surface px-3 text-sm text-fg placeholder:text-fg-muted focus-ring disabled:cursor-not-allowed disabled:opacity-60";

/**
 * Class string for text-like controls (input, select, textarea, picker triggers).
 * @param {object} [options]
 * @param {boolean} [options.invalid=false] Danger border instead of the default line border.
 * @param {boolean} [options.autoHeight=false] Vertical padding instead of the fixed h-10 (textarea, auto-height select).
 * @param {string} [options.className] Extra classes appended last.
 */
export function getControlClassName({
  invalid = false,
  autoHeight = false,
  className = "",
} = {}) {
  return cx(
    CONTROL_BASE,
    autoHeight ? "py-2" : "h-10",
    invalid ? "border-danger" : "border-line",
    className
  );
}

/** Default control style: `h-10 w-full rounded-control border border-line bg-surface px-3 text-sm text-fg …`. */
export const controlClassName = getControlClassName();

/**
 * Forwards `touched` only when the caller supplied the prop, so Formik's
 * `touched={touched.email}` (undefined before blur) keeps the error hidden
 * while omitting `touched` entirely shows the error immediately.
 */
export const pickTouched = (props) =>
  "touched" in props ? { touched: props.touched } : {};

/**
 * Label + hint + error wrapper that wires accessibility attributes into its control.
 *
 * Props:
 * - id: control id; generated with useId when omitted.
 * - label: visible label text (node).
 * - required=false: shows an asterisk and sets aria-required on the control.
 * - hint: helper text under the control.
 * - error: error message.
 * - touched: when the prop is present, the error only shows while truthy; when absent, the error shows whenever set.
 * - invalid=false: marks the control invalid without a message (e.g. legacy `state="error"`).
 * - className: wrapper classes. labelClassName: extra label classes.
 * - children: a single element (cloned with the control props) or
 *   `(controlProps) => element`, where controlProps = { id, "aria-invalid", "aria-describedby", "aria-required" }.
 */
function FormField(props) {
  const {
    id,
    label,
    required = false,
    hint,
    error,
    touched,
    invalid = false,
    className = "",
    labelClassName = "",
    children,
  } = props;

  const autoId = useId();
  const controlId = id || autoId;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = `${controlId}-error`;
  const showError =
    Boolean(error) && ("touched" in props ? Boolean(touched) : true);

  const controlProps = {
    id: controlId,
    "aria-invalid": showError || invalid ? true : undefined,
    "aria-describedby": cx(hintId, showError && errorId) || undefined,
    "aria-required": required || undefined,
  };

  let control = null;
  if (typeof children === "function") {
    control = children(controlProps);
  } else if (isValidElement(children)) {
    control = cloneElement(children, {
      ...controlProps,
      id: children.props.id || controlId,
      "aria-describedby":
        cx(children.props["aria-describedby"], controlProps["aria-describedby"]) ||
        undefined,
    });
  }

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={controlId}
          className={cx("mb-1.5 block text-sm font-medium text-fg", labelClassName)}
        >
          {label}
          {required && (
            <span aria-hidden="true" className="ml-0.5 text-danger-fg">
              *
            </span>
          )}
        </label>
      )}
      {control}
      {hint && (
        <p id={hintId} className="mt-1 text-xs text-fg-muted">
          {hint}
        </p>
      )}
      {showError && (
        <p id={errorId} className="mt-1 text-xs text-danger-fg">
          {error}
        </p>
      )}
    </div>
  );
}

export default FormField;
